import { api } from '../lib/api';
import type { Asset, AssetStatus, DeliverableLine } from '../types/api';

export async function listDeliverable(): Promise<DeliverableLine[]> {
  const { data } = await api.get<DeliverableLine[]>('/assets/deliverable');
  return data;
}

export interface DeliverOrderLineInput {
  orderLineId: string;
  serialNumber?: string;
  condition?: string;
  warrantyEndDate?: string;
  installDate?: string;
  currentSiteId?: string;
  currentUserId?: string;
}

export async function deliverOrderLine(input: DeliverOrderLineInput): Promise<Asset> {
  const { data } = await api.post<Asset>('/assets/deliver', input);
  return data;
}

export interface ListAssetsQuery {
  customerOrganisationId?: string;
  siteId?: string;
  status?: AssetStatus;
}

export async function listAssets(query: ListAssetsQuery = {}): Promise<Asset[]> {
  const params = new URLSearchParams();
  if (query.customerOrganisationId) params.set('customerOrganisationId', query.customerOrganisationId);
  if (query.siteId) params.set('siteId', query.siteId);
  if (query.status) params.set('status', query.status);
  const { data } = await api.get<Asset[]>(`/assets?${params.toString()}`);
  return data;
}

export async function getAsset(id: string): Promise<Asset> {
  const { data } = await api.get<Asset>(`/assets/${id}`);
  return data;
}

// SPARE assets at a site that are eligible to be requested for loan - open
// to End Users (unlike listAssets above), backing the "Request a loan" flow.
export async function listLoanableAssets(siteId: string): Promise<Asset[]> {
  const { data } = await api.get<Asset[]>(`/assets/loanable/${siteId}`);
  return data;
}

export async function returnAsset(id: string, notes?: string): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/return`, { notes });
  return data;
}

export async function decommissionAsset(id: string, notes?: string): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/decommission`, { notes });
  return data;
}

export interface ReassignAssetInput {
  toSiteId?: string;
  toUserId?: string;
  notes?: string;
}

export async function reassignAsset(id: string, input: ReassignAssetInput): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/reassign`, input);
  return data;
}

export async function updateAssetCondition(id: string, condition: string, notes?: string): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/condition`, { condition, notes });
  return data;
}

export interface LogServiceInput {
  description: string;
  cost?: number;
  vendor?: string;
  occurredAt?: string;
}

export async function logAssetService(id: string, input: LogServiceInput): Promise<Asset> {
  const { data } = await api.post<Asset>(`/assets/${id}/service`, input);
  return data;
}

export interface UpdateAssetDetailsInput {
  // undefined = leave unchanged, null = clear.
  warrantyEndDate?: string | null;
  serialNumber?: string;
  purchaseDate?: string | null;
  purchasePrice?: number | null;
  termMonths?: number | null;
}

export async function updateAssetDetails(id: string, input: UpdateAssetDetailsInput): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/details`, input);
  return data;
}
